/**
 * splitRuleHelpers — Story 2.3-UI (Split-Editor)
 *
 * Reine Helfer ohne React: Status-Ableitung einer Regel aus start_date/end_date
 * (der Contract kennt kein explizites Status-Feld) und das Herausziehen der
 * lokalisierten BE-Fehlermeldung aus dem v2-ErrorBody ({ error: { message } }).
 */
import { ApiError } from '../../lib/errors/ApiError'
import type { HostSplitRule } from './useSplitRules'

// --- Status ----------------------------------------------------------------

export type SplitRuleStatus = 'active' | 'scheduled' | 'ended'

/**
 * Leitet den Anzeigestatus einer Regel aus ihrem Gueltigkeitsfenster ab:
 * - start_date in der Zukunft  -> 'scheduled'
 * - end_date in der Vergangenheit -> 'ended'
 * - sonst -> 'active' (offene Enden zaehlen als unbegrenzt gueltig)
 */
export function splitRuleStatus(
  rule: Pick<HostSplitRule, 'start_date' | 'end_date'>,
  today: Date = new Date(),
): SplitRuleStatus {
  // Datumsvergleich auf Tagesebene ueber ISO-Strings (YYYY-MM-DD sortiert lexikografisch).
  const iso = toIsoDay(today)
  if (rule.start_date && rule.start_date > iso) return 'scheduled'
  if (rule.end_date && rule.end_date < iso) return 'ended'
  return 'active'
}

function toIsoDay(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

// --- Eingabe-Parsing -------------------------------------------------------

/**
 * Parst eine Formulareingabe als Dezimalzahl. Akzeptiert Komma als
 * Dezimaltrenner (DE-Eingabe "0,11"), liefert null fuer leere/unparsbare
 * Eingaben — Werte wie 0 kommen bewusst durch, damit das Backend den
 * ZeroRate-Fall als 422 beantworten kann.
 */
export function parseDecimal(raw: string): number | null {
  const trimmed = raw.trim()
  if (trimmed === '') return null
  const n = Number(trimmed.replace(',', '.'))
  return Number.isFinite(n) ? n : null
}

// --- Formular-Validierung (clientseitig) -----------------------------------

/**
 * i18n-Schluessel (Namespace `splits`) der clientseitigen Formularfehler.
 * Die Pruefung ist bewusst nur ein Vorfilter: das 422 des Backends bleibt der
 * autoritative Fallback (z.B. Summe ueberlappender %-Splits > 100 %).
 */
export type SplitRuleFormErrorKey =
  | 'validation.valueRequired'
  | 'validation.percentageRange'
  | 'validation.fixedPositive'
  | 'validation.endBeforeStart'

export type SplitRuleFormErrors = {
  /** Fehler am Wert-Feld (Prozent bzw. Fixbetrag). */
  value?: SplitRuleFormErrorKey
  /** Fehler am Feld „Gueltig bis“. */
  end_date?: SplitRuleFormErrorKey
}

export type SplitRuleFormInput = {
  split_type: 'percentage' | 'fixed_amount'
  value: string
  start_date: string
  end_date: string
}

/**
 * Prueft die Formularwerte, bevor gespeichert wird:
 * - percentage: > 0 (min exklusiv) und <= 100
 * - fixed_amount: > 0
 * - end_date >= start_date (gleicher Tag ist erlaubt)
 * Leere Datumsfelder sind gueltig (offenes Gueltigkeitsfenster).
 */
export function validateSplitRuleForm(input: SplitRuleFormInput): SplitRuleFormErrors {
  const errors: SplitRuleFormErrors = {}

  const value = parseDecimal(input.value)
  if (value == null) {
    errors.value = 'validation.valueRequired'
  } else if (input.split_type === 'percentage') {
    if (value <= 0 || value > 100) errors.value = 'validation.percentageRange'
  } else if (value <= 0) {
    errors.value = 'validation.fixedPositive'
  }

  // YYYY-MM-DD sortiert lexikografisch korrekt (native date-Inputs liefern ISO).
  if (input.start_date && input.end_date && input.end_date < input.start_date) {
    errors.end_date = 'validation.endBeforeStart'
  }

  return errors
}

// --- BE-Fehlermeldungen ----------------------------------------------------

type ErrorBodyShape = {
  error?: {
    code?: string
    message?: string
    details?: Record<string, unknown>
  }
}

/**
 * Zieht die lokalisierte Meldung aus einem v2-ErrorBody (z.B. 422 sum-overlap).
 * Feld-Details (ActiveModel::Errors#as_json) werden angehaengt, damit z.B.
 * "percentage: Summe ueberlappender Splits ..." sichtbar wird.
 * Liefert null, wenn keine verwertbare Meldung im Fehler steckt.
 */
export function apiErrorMessage(error: unknown): string | null {
  if (!(error instanceof ApiError)) return null
  const body = error.body as ErrorBodyShape | null | undefined
  const err = body?.error
  if (!err) return null

  const parts: string[] = []
  if (typeof err.message === 'string' && err.message.length > 0) parts.push(err.message)

  if (err.details && typeof err.details === 'object') {
    for (const [field, messages] of Object.entries(err.details)) {
      const list = Array.isArray(messages) ? messages : [messages]
      for (const m of list) {
        if (typeof m === 'string' && m.length > 0) parts.push(`${field}: ${m}`)
      }
    }
  }

  return parts.length > 0 ? parts.join(' · ') : null
}

/** true, wenn der Fehler ein 422-Validierungsfehler des Backends ist. */
export function isValidationError(error: unknown): boolean {
  return error instanceof ApiError && error.status === 422
}
